import React, { useCallback, useEffect, useState } from "react";
import {
  Opacity as OpacityIcon,
  Spa as SpaIcon,
  Weekend as WeekendIcon,
  ArrowForward as ArrowForwardIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
} from "@material-ui/icons";

import styles from "./ServicosAbraCasa.module.css";

// O site só pode ser embutido depois que liberar o domínio do AbraHub em
// `Content-Security-Policy: frame-ancestors` (hoje responde X-Frame-Options: SAMEORIGIN).
const IFRAME_ENABLED = process.env.REACT_APP_SERVICOS_IFRAME === "true";

type ServicosContext = "tracking" | "lista";

interface ServicosAbraCasaProps {
  context: ServicosContext;
}

const servicos = [
  {
    icon: OpacityIcon,
    title: "Impermeabilização",
    description: "Proteção contra manchas e líquidos para sofás, poltronas e colchões.",
    region: "RJ e SP",
    imageClass: styles.cardImpermeabilizacao,
    url: "https://www.abracasa.com.br/servicos/impermeabilizacao",
  },
  {
    icon: SpaIcon,
    title: "Higienização",
    description: "Limpeza profissional que renova o estofado sem trocar de móvel.",
    region: "RJ",
    imageClass: styles.cardHigienizacao,
    url: "https://www.abracasa.com.br/servicos/higienizacao",
  },
  {
    icon: WeekendIcon,
    title: "Montagem",
    description: "Montagem opcional depois da entrega, com data agendada. O valor varia conforme o produto e a região.",
    region: "RJ e SP",
    imageClass: styles.cardMontagem,
    url: "https://www.abracasa.com.br/servicos/montagem",
  },
];

const copy: Record<ServicosContext, { eyebrow: string; title: string; subtitle: string }> = {
  tracking: {
    eyebrow: "Cuidados para a sua casa",
    title: "Seu móvel novo chegando. E os que você já tem?",
    subtitle:
      "A Abra Casa agora também cuida dos móveis que já estão com você. Preço na hora, sem precisar sair daqui.",
  },
  lista: {
    eyebrow: "Novo na Abra Casa",
    title: "Cuidado profissional para os móveis que você já tem",
    subtitle:
      "Impermeabilização, higienização e montagem para os móveis da sua casa.",
  },
};

function buildUrl(baseUrl: string, context: ServicosContext) {
  const params = new URLSearchParams({
    utm_source: "abrahub",
    utm_medium: context === "tracking" ? "pagina_pedido" : "meus_pedidos",
    utm_campaign: "abracasa_servicos",
  });
  return `${baseUrl}?${params.toString()}`;
}

const WHATSAPP_NUMBER = "5521971174398";

function openServicosWhatsApp() {
  const message = "Olá! Gostaria de informações sobre Venda de Serviços - Impermeabilização e Higienização (opção 7).";
  const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  window.open(url, "_blank");
}

export const SERVICOS_SECTION_ID = "servicos-abracasa";

// Desconta o header fixo da tela /meus-pedidos para a seção não ficar escondida atrás dele.
const SCROLL_OFFSET = 96;

export function scrollToServicos() {
  const section = document.getElementById(SERVICOS_SECTION_ID);
  if (!section) return;

  const top = section.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
  window.scrollTo({ top, behavior: "smooth" });

  section.classList.remove(styles.highlight);
  // Força reflow para a animação reiniciar em cliques seguidos
  void section.offsetWidth;
  section.classList.add(styles.highlight);
}

function ServicosAbraCasa({ context }: ServicosAbraCasaProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [activeUrl, setActiveUrl] = useState("");
  const texts = copy[context];

  const open = useCallback((baseUrl: string) => {
    const destination = buildUrl(baseUrl, context);
    if (!IFRAME_ENABLED) {
      window.open(destination, "_blank", "noopener,noreferrer");
      return;
    }
    setActiveUrl(destination);
    setIframeLoaded(false);
    setIsOpen(true);
  }, [context]);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, close]);

  return (
    <>
      <section
        id={SERVICOS_SECTION_ID}
        className={styles.section}
        aria-labelledby={`servicos-${context}-title`}
      >
        <div className={styles.header}>
          <span className={styles.eyebrow}>{texts.eyebrow}</span>
          <h3 id={`servicos-${context}-title`} className={styles.title}>
            {texts.title}
          </h3>
          <p className={styles.subtitle}>{texts.subtitle}</p>
        </div>

        <div className={styles.grid}>
          {servicos.map(({ icon: Icon, title, description, region, imageClass, url }) => (
            <button key={title} type="button" className={`${styles.card} ${imageClass}`} onClick={() => open(url)}>
              <span className={styles.cardIcon}>
                <Icon />
              </span>
              <span className={styles.cardBody}>
                <span className={styles.cardTitle}>{title}</span>
                <span className={styles.cardDescription}>{description}</span>
              </span>
              <span className={styles.cardFooter}>
                <span className={styles.cardRegion}>{region}</span>
                <span className={styles.cardLink}>
                  Ver preço <ArrowForwardIcon />
                </span>
              </span>
            </button>
          ))}
        </div>

        <p className={styles.whatsappNote}>
          Ou{" "}
          <button type="button" className={styles.whatsappLink} onClick={openServicosWhatsApp}>
            clique aqui
          </button>{" "}
          e entre em contato conosco através do nosso WhatsApp, informando a opção 7
          (Venda de Serviços - Impermeabilização e Higienização).
        </p>
      </section>

      {isOpen && (
        <div className={styles.overlay} onClick={close} role="presentation">
          <div
            className={styles.modal}
            role="dialog"
            aria-modal="true"
            aria-label="Abra Casa Serviços"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalBar}>
              <span className={styles.modalTitle}>Abra Casa Serviços</span>
              <div className={styles.modalActions}>
                <a
                  href={activeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.modalAction}
                  aria-label="Abrir em nova aba"
                >
                  <OpenInNewIcon />
                </a>
                <button
                  type="button"
                  className={styles.modalAction}
                  onClick={close}
                  aria-label="Fechar"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>
            {!iframeLoaded && <div className={styles.modalLoading}>Carregando serviços…</div>}
            <iframe
              title="Abra Casa Serviços"
              src={activeUrl}
              className={styles.iframe}
              onLoad={() => setIframeLoaded(true)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default ServicosAbraCasa;
